import React, { useEffect, useState } from "react";
import Navbar from "../components/jobsiq/Navbar";
import Footer from "../components/jobsiq/Footer";
import JobCard from "../components/jobsiq/JobCard";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MagnifyingGlass, Funnel } from "@phosphor-icons/react";

export default function JobsList() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [q, setQ] = useState("");
    const [location, setLocation] = useState("");
    const [jobType, setJobType] = useState("");
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const params = {};
        if (q) params.q = q;
        if (location) params.location = location;
        if (jobType) params.job_type = jobType;
        const { data } = await api.get("/jobs", { params });
        setJobs(data);
        setLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, []);

    const apply = async (job) => {
        if (!user) {
            toast.message("Sign in to apply");
            nav("/login");
            return;
        }
        if (user.role !== "seeker") {
            toast.error("Only job seeker accounts can apply");
            return;
        }
        try {
            await api.post(`/applications/${job.job_id}`);
            toast.success(`Applied! Match scored by AI.`);
            nav("/dashboard");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Apply failed");
        }
    };

    const toggleSave = async (job) => {
        if (!user) {
            toast.message("Sign in to save jobs");
            nav("/login");
            return;
        }
        if (user.role !== "seeker") return;
        try {
            if (job.is_saved) {
                await api.delete(`/saved-jobs/${job.job_id}`);
            } else {
                await api.post(`/saved-jobs/${job.job_id}`);
            }
            setJobs((js) => js.map((j) => (j.job_id === job.job_id ? { ...j, is_saved: !j.is_saved } : j)));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Action failed");
        }
    };

    return (
        <div data-testid="jobs-list-page">
            <Navbar />
            <section className="border-b border-slate-200 bg-slate-100">
                <div className="max-w-[1400px] mx-auto px-6 py-10">
                    <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-600 mb-2">
                        Opportunities
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl">Find the role the model was waiting for.</h1>

                    <div className="mt-6 bg-white border border-slate-300 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto]">
                        <div className="flex items-center gap-2 px-3 border-b md:border-b-0 md:border-r border-slate-200">
                            <MagnifyingGlass size={18} className="text-slate-400" />
                            <input
                                data-testid="job-search-input"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Title, skill, keyword"
                                className="w-full py-3 outline-none"
                            />
                        </div>
                        <input
                            data-testid="job-location-input"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            placeholder="Location"
                            className="w-full py-3 px-3 outline-none border-b md:border-b-0 md:border-r border-slate-200"
                        />
                        <select
                            data-testid="job-type-select"
                            value={jobType}
                            onChange={(e) => setJobType(e.target.value)}
                            className="w-full py-3 px-3 outline-none border-b md:border-b-0 md:border-r border-slate-200"
                        >
                            <option value="">Any type</option>
                            <option value="full-time">Full-time</option>
                            <option value="part-time">Part-time</option>
                            <option value="contract">Contract</option>
                            <option value="internship">Internship</option>
                        </select>
                        <button
                            onClick={load}
                            data-testid="job-search-submit"
                            className="bg-slate-950 text-white px-6 hover:bg-blue-600 inline-flex items-center gap-2 justify-center py-3"
                        >
                            <Funnel size={16} /> Search
                        </button>
                    </div>
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-6 py-10">
                <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-4">
                    {loading ? "Searching…" : `${jobs.length} result${jobs.length === 1 ? "" : "s"}`}
                </div>
                <div className="grid grid-cols-1 gap-4">
                    {jobs.map((j) => (
                        <JobCard
                            key={j.job_id}
                            job={j}
                            currentUser={user}
                            onApply={user?.role !== "company" ? apply : null}
                            onToggleSave={toggleSave}
                        />
                    ))}
                    {!loading && jobs.length === 0 && (
                        <div className="border border-dashed border-slate-300 p-12 text-center text-slate-500 text-sm">
                            No roles match those filters.
                        </div>
                    )}
                </div>
            </section>
            <Footer />
        </div>
    );
}
